// Live annualized-pay readout shared by the VoE employer form and the analyst's call log. It mirrors
// Voe::PayAnnualizer for display only (the server stores the authoritative figure), so keep the
// multiplier in sync with PayAnnualizer.
const WEEKS_PER_YEAR = 52;

export function parseAmount(raw) {
  return parseFloat(String(raw).replace(/[$,\s]/g, ""));
}

// A positive Number, or null when the pay can't be annualized (no or invalid amount, no structure,
// hourly with no hours).
export function annualize(structure, amount, hours) {
  if (!(amount > 0)) return null;
  if (structure === "hourly") return hours > 0 ? amount * hours * WEEKS_PER_YEAR : null;
  if (structure === "salary") return amount;
  return null;
}

// The math breakdown line; empty for salary, whose amount is already the annual figure.
export function formulaText(structure, amount, hours) {
  if (structure !== "hourly") return "";

  return `${currency(amount, true)}/hr × ${hours} hrs/wk × ${WEEKS_PER_YEAR} wks`;
}

// Analyst-form checks, stricter than parseAmount/parseFloat (which read "12abc" as 12) so a typo is flagged
// before submit. The server (Voe::VerifiedAnswer) stays the authority; these return the message or null.
const MAX_PAY_AMOUNT = 99999999.99;
const MAX_HOURS_PER_WEEK = 168;

export function amountProblem(raw) {
  const cleaned = String(raw).replace(/[$,\s]/g, "");
  if (cleaned === "") return null;

  const amount = /^(\d+\.?\d*|\.\d+)$/.test(cleaned) ? parseFloat(cleaned) : NaN;
  return amount > 0 && amount <= MAX_PAY_AMOUNT ? null : "Enter the amount as a positive number.";
}

// A number input reports "" for text it can't read, so badInput (from its validity) catches "abc".
export function hoursProblem(raw, badInput) {
  const message = `Enter whole hours between 1 and ${MAX_HOURS_PER_WEEK}.`;
  if (badInput) return message;

  const cleaned = String(raw).trim();
  if (cleaned === "") return null;

  const hours = /^\d+$/.test(cleaned) ? parseInt(cleaned, 10) : NaN;
  return hours >= 1 && hours <= MAX_HOURS_PER_WEEK ? null : message;
}

// Shows or clears a field's inline message; the custom validity also holds the submit until it's fixed.
// While the analyst is typing, a new message waits for the field's change (so "0" on the way to "0.5" isn't
// flagged), but one already showing updates or clears as they type.
export function flag(input, messageElement, message, typing) {
  input.setCustomValidity(message || "");
  if (typing && message && !input.classList.contains("is-invalid")) return;

  messageElement.textContent = message || "";
  input.classList.toggle("is-invalid", Boolean(message));
  input.setAttribute("aria-invalid", String(Boolean(message)));
}

export function amountLabel(structure) {
  if (structure === "hourly") return "Hourly rate";
  if (structure === "salary") return "Annual salary";

  return "Pay amount";
}

export function promptText(structure) {
  return structure === null
    ? "Choose Hourly or Salary above to calculate the annualized income."
    : "Enter the pay amount to see the annualized income.";
}

export function currency(number, cents = false) {
  return number.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: cents ? 2 : 0,
    maximumFractionDigits: cents ? 2 : 0,
  });
}
